import { differenceInMonths } from "date-fns";
import {
  amortizationSchedule,
  annualCostForExpenseCategories,
  type InvestmentProperty
} from "@aus-property/core";
import { estimateValueToday, estimateWeeklyRentToday } from "@/utils/propertyEstimates";

export interface PastPerformanceInput {
  purchasePrice: number;
  purchaseDate: string;
  currentValue: number;
  currentLoanBalance: number;
  currentInterestRate: number;
  weeklyRent: number;
  annualNonDebtCosts: number;
  vacancyRate: number;
  monthlyRepayment: number;
  initialCashInvested: number;
  benchmarkGrowthRate?: number;
}

export interface PastPerformanceAnalysis {
  yearsHeld: number;
  capitalGain: number;
  capitalGrowthPercent: number;
  capitalCagrPercent: number;
  currentLoanBalance: number;
  equityNow: number;
  equityGainSincePurchase: number;
  loanToValueRatio: number;
  effectiveAnnualRent: number;
  annualDebtService: number;
  annualNonDebtCosts: number;
  annualNetCashflow: number;
  totalReturnApprox: number;
  debtCoverageRatio: number;
  breakEvenWeeklyRent: number;
  equityMultiple: number;
  performanceScore: number;
  scoreBand: "High performing" | "Stable" | "Needs attention";
  insights: string[];
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const toYearsHeld = (purchaseDateIso: string, now = new Date()): number => {
  const purchase = new Date(purchaseDateIso);
  if (Number.isNaN(purchase.getTime())) return 0;
  return Math.max(0, differenceInMonths(now, purchase) / 12);
};

export const calculatePastPerformance = (
  input: PastPerformanceInput,
  now = new Date()
): PastPerformanceAnalysis => {
  const yearsHeld = toYearsHeld(input.purchaseDate, now);
  const safeYears = Math.max(yearsHeld, 1 / 12);
  const benchmarkGrowthRate = input.benchmarkGrowthRate ?? 5;

  const capitalGain = input.currentValue - input.purchasePrice;
  const capitalGrowthPercent =
    input.purchasePrice > 0 ? (capitalGain / input.purchasePrice) * 100 : 0;
  const capitalCagrPercent =
    input.purchasePrice > 0 && input.currentValue > 0
      ? (Math.pow(input.currentValue / input.purchasePrice, 1 / safeYears) - 1) * 100
      : 0;

  const vacancyFactor = clamp(1 - input.vacancyRate / 100, 0, 1);
  const effectiveAnnualRent = input.weeklyRent * 52 * vacancyFactor;
  const annualDebtService = Math.max(input.monthlyRepayment, 0) * 12;
  const annualNonDebtCosts = Math.max(input.annualNonDebtCosts, 0);
  const annualNetCashflow = effectiveAnnualRent - annualNonDebtCosts - annualDebtService;
  const debtCoverageRatio =
    (effectiveAnnualRent - annualNonDebtCosts) / Math.max(1, annualDebtService);
  const breakEvenWeeklyRent =
    (annualNonDebtCosts + annualDebtService) / Math.max(1, 52 * vacancyFactor);

  const equityNow = input.currentValue - input.currentLoanBalance;
  const equityGainSincePurchase = equityNow - input.initialCashInvested;
  const loanToValueRatio =
    input.currentValue > 0 ? (input.currentLoanBalance / input.currentValue) * 100 : 0;
  const equityMultiple =
    input.initialCashInvested > 0 ? equityNow / input.initialCashInvested : 0;
  const totalReturnApprox = capitalGain + annualNetCashflow * safeYears;

  const growthScore = clamp(((capitalCagrPercent - 1.2) / 8.5) * 36, 0, 36);
  const cashflowScore = clamp((annualNetCashflow / Math.max(input.purchasePrice, 1)) * 2200 + 10, 0, 24);
  const resilienceScore = clamp((debtCoverageRatio - 0.9) * 32, 0, 24);
  const equityScore = clamp((equityMultiple - 1) * 11.5, 0, 18);
  const leveragePenalty = clamp((loanToValueRatio - 80) * 0.9, 0, 10);

  const performanceScore = Math.round(
    clamp(growthScore + cashflowScore + resilienceScore + equityScore - leveragePenalty, 0, 100)
  );

  const scoreBand =
    performanceScore >= 72
      ? "High performing"
      : performanceScore >= 48
        ? "Stable"
        : "Needs attention";

  const insights: string[] = [];

  if (capitalCagrPercent >= benchmarkGrowthRate + 1) {
    insights.push(
      `Capital growth is outpacing the ${benchmarkGrowthRate.toFixed(1)}% benchmark by ${(capitalCagrPercent - benchmarkGrowthRate).toFixed(1)}%/yr.`
    );
  } else if (capitalCagrPercent < benchmarkGrowthRate - 1) {
    insights.push(
      `Capital growth is trailing the ${benchmarkGrowthRate.toFixed(1)}% benchmark by ${(benchmarkGrowthRate - capitalCagrPercent).toFixed(1)}%/yr.`
    );
  } else {
    insights.push(
      `Capital growth is tracking close to the ${benchmarkGrowthRate.toFixed(1)}% benchmark.`
    );
  }

  if (annualNetCashflow >= 0) {
    insights.push(
      `The property is approximately cashflow-positive by $${Math.round(annualNetCashflow).toLocaleString()} per year.`
    );
  } else {
    insights.push(
      `The property is currently carrying a yearly cashflow drag of $${Math.round(Math.abs(annualNetCashflow)).toLocaleString()}.`
    );
  }

  if (debtCoverageRatio < 1) {
    insights.push(
      "Rent after operating costs is not fully covering debt service. This holding is sensitive to rate and vacancy shocks."
    );
  } else if (debtCoverageRatio < 1.2) {
    insights.push(
      "Debt coverage is thin. Small rent softness or cost inflation can compress holding margin."
    );
  } else {
    insights.push("Debt coverage is healthy, giving stronger holding resilience.");
  }

  if (loanToValueRatio > 80) {
    insights.push(
      "Current leverage is elevated (>80% LVR). Prioritise equity buffer growth and refinancing flexibility."
    );
  } else if (loanToValueRatio < 65) {
    insights.push(
      "Leverage is moderate with a usable equity buffer for refinancing or acquisition optionality."
    );
  }

  if (breakEvenWeeklyRent > input.weeklyRent * 1.05) {
    insights.push(
      "Break-even rent is above current rent, indicating a narrow holding margin at today's debt and cost settings."
    );
  }

  return {
    yearsHeld,
    capitalGain,
    capitalGrowthPercent,
    capitalCagrPercent,
    currentLoanBalance: input.currentLoanBalance,
    equityNow,
    equityGainSincePurchase,
    loanToValueRatio,
    effectiveAnnualRent,
    annualDebtService,
    annualNonDebtCosts,
    annualNetCashflow,
    totalReturnApprox,
    debtCoverageRatio,
    breakEvenWeeklyRent,
    equityMultiple,
    performanceScore,
    scoreBand,
    insights
  };
};

const estimateCurrentLoanSnapshot = (
  property: InvestmentProperty,
  now = new Date()
): { balance: number; monthlyRepayment: number } => {
  const purchaseDate = new Date(property.purchase.purchaseDate);
  const monthsHeld = Number.isNaN(purchaseDate.getTime())
    ? 0
    : Math.max(0, differenceInMonths(now, purchaseDate));

  const scheduleYears = Math.max(
    property.loan.termYears,
    Math.ceil(monthsHeld / 12) + 1
  );
  const schedule = amortizationSchedule(property, scheduleYears);
  const index = Math.min(monthsHeld, Math.max(0, schedule.length - 1));
  const point = schedule[index];

  return {
    balance: point?.remainingBalance ?? property.loan.principal,
    monthlyRepayment: point?.totalPayment ?? 0
  };
};

export const buildPastPerformanceInputFromProperty = (
  property: InvestmentProperty,
  now = new Date()
): PastPerformanceInput => {
  const loanSnapshot = estimateCurrentLoanSnapshot(property, now);
  const yearsHeld = toYearsHeld(property.purchase.purchaseDate, now);
  const yearIndex = Math.max(1, Math.floor(yearsHeld) + 1);

  const effectiveAnnualRent =
    estimateWeeklyRentToday(property, now) *
    52 *
    clamp(1 - property.expenses.vacancyRate / 100, 0, 1);

  const baseFixedCosts =
    property.expenses.agentFeesAnnual +
    property.expenses.councilRates +
    property.expenses.insurance +
    property.expenses.maintenance +
    property.expenses.strata +
    property.expenses.water +
    property.expenses.utilities;

  const inflatedFixedCosts =
    baseFixedCosts *
    Math.pow(1 + property.expenses.expenseInflationRate / 100, yearsHeld);
  const categoryCosts = annualCostForExpenseCategories(
    property.expenses.categories,
    yearIndex,
    property.expenses.expenseInflationRate
  );
  const managementCost =
    effectiveAnnualRent * (property.expenses.managementPercent / 100);

  const initialCashInvested =
    property.purchase.deposit +
    property.purchase.stampDuty +
    property.purchase.legalFees +
    property.purchase.inspections +
    property.purchase.lmi +
    property.purchase.renovationCapex;

  return {
    purchasePrice: property.purchase.price,
    purchaseDate: property.purchase.purchaseDate,
    currentValue: estimateValueToday(property, now),
    currentLoanBalance: loanSnapshot.balance,
    currentInterestRate: property.loan.interestRate,
    weeklyRent: estimateWeeklyRentToday(property, now),
    annualNonDebtCosts: inflatedFixedCosts + categoryCosts + managementCost,
    vacancyRate: property.expenses.vacancyRate,
    monthlyRepayment: loanSnapshot.monthlyRepayment,
    initialCashInvested,
    benchmarkGrowthRate: 5
  };
};

export const analyzePropertyPastPerformance = (
  property: InvestmentProperty,
  now = new Date()
): PastPerformanceAnalysis =>
  calculatePastPerformance(buildPastPerformanceInputFromProperty(property, now), now);
